import { type CompanyOpenOpportunityListV2_organization$key } from '#app/__generated__/relay/CompanyOpenOpportunityListV2_organization.graphql.js';
import { type CompanyOpenOpportunityListV2Pagination_organization$key } from '#app/__generated__/relay/CompanyOpenOpportunityListV2Pagination_organization.graphql.js';
import CompanyOpenOpportunityListV2QueryNode, {
  type CompanyOpenOpportunityListV2Query as CompanyOpenOpportunityListV2QueryType,
} from '#app/__generated__/relay/CompanyOpenOpportunityListV2Query.graphql.js';
import { JobPostingCard } from '#app/components/JobPostingCard/JobPostingCard.js';
import { Text } from '#app/components/Primitives/Text.js';
import { NewWorkOpportunitySkeleton } from '#app/components/SkeletonLoaders/NewWorkOpportunitySkeleton.js';
import { useNewJob } from '#app/features/job/hooks/useNewJob.js';
import { WorkTabLayoutContextProvider } from '#app/features/paidProjects/contexts/WorkTabLayoutContext.js';
import { usePaginatedInfiniteLoad } from '#app/hooks/usePaginatedInfiniteLoad.js';
import { OverviewJobSectionEmptyState } from '../EmptyStates/OverviewJobSectionEmptyState.js';
import { AddIcon } from '@contra/icons/icons';
import { Money } from '@contra/money';
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
} from '@contra/relay';
import { isTruthy } from '@contra/utilities/isTruthy';
import { Fragment, useCallback, useMemo } from 'react';
import { css, styled } from 'styled-components';

const OpportunityList = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 16px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colorsV2.gray30};
  }
`;

const ItemNumber = styled(Text)`
  font-size: 36px;
  font-weight: 300;
  line-height: 51px;
  letter-spacing: -0.94px;
  text-align: left;
  color: ${({ theme }) => theme.colorsV2.gray90};
`;

const ItemDescription = styled(Text)`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }) => theme.colorsV2.gray70};
`;

const Grid = styled.div<{ $columns: string }>`
  ${({ $columns, theme }) => css`
    display: grid;
    grid-template-columns: repeat(${$columns}, 1fr);
    padding: 24px 0;
    min-width: 400px;
    border: 1px solid ${theme.colorsV2.gray30};
    border-radius: 20px;
  `}
`;
const Divider = styled.div`
  ${({ theme }) => css`
    height: 1px;
    background-color: ${theme.colorsV2.gray30};
    width: 95%;
    margin: 0 auto;
  `}
`;

const List = styled.div`
  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      border: 1px solid ${theme.colorsV2.gray30};
      border-radius: 20px;
    }
  `}
`;

const Overflow = styled.div`
  overflow-x: auto;
`;

const PostAJobContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 16px;
    padding: 24px;
    width: 100%;
    border-radius: 20px;
    background: ${theme.colorsV2.gray05};
  `}
`;

const PlusButton = styled.button`
  ${({ theme }) => css`
    background: ${theme.colorsV2.white};
    border-radius: 50%;
    border: 1px solid ${theme.colorsV2.gray80};
    color: ${theme.colorsV2.gray80};
    transition: background-color 175ms ease;
    transition-property: background-color, border, color;
    width: 48px;
    height: 48px;

    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      cursor: pointer;
      border-color: ${theme.colorsV2.gray70};
      color: ${theme.colorsV2.gray70};
      background-color: ${theme.colorsV2.gray10};
    }
  `}
`;

type OpenOpportunityListProps = {
  readonly organizationRef: CompanyOpenOpportunityListV2_organization$key;
};

export const CompanyOpenOpportunityListV2 = ({
  organizationRef,
}: OpenOpportunityListProps) => {
  const { onClick: onClickPostJob } = useNewJob({
    analyticsEvent: [
      'button',
      {
        action: 'tapped',
        button_name: 'company_profile_owner_post_job',
      },
    ],
  });
  const organizationData =
    useFragment<CompanyOpenOpportunityListV2_organization$key>(
      graphql`
        fragment CompanyOpenOpportunityListV2_organization on Organization {
          id
          averageJobValue
          completedJobsCount
          visitorCanEdit
          visitorIsMember
          ...OverviewJobSectionEmptyState_organization
        }
      `,
      organizationRef,
    );

  const { id, visitorCanEdit } = organizationData;

  const query = useLazyLoadQuery<CompanyOpenOpportunityListV2QueryType>(
    CompanyOpenOpportunityListV2QueryNode,
    {
      id,
    },
    { fetchPolicy: 'store-and-network' },
  );

  const {
    data: jobPostingData,
    hasNext,
    isLoadingNext,
    loadNext,
  } = usePaginationFragment<
    CompanyOpenOpportunityListV2QueryType,
    CompanyOpenOpportunityListV2Pagination_organization$key
  >(
    graphql`
      fragment CompanyOpenOpportunityListV2Pagination_organization on Organization
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 1 }
        after: { type: "String" }
      )
      @refetchable(queryName: "CompanyOpenOpportunityListV2Query") {
        jobs(first: $first, after: $after, filter: { statuses: [POSTED] })
          @connection(key: "CompanyOpenOpportunityListV2_jobs") {
          count
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          edges {
            node {
              jobPosting {
                id
                ...JobPostingCard_jobPosting
              }
            }
          }
        }
      }
    `,
    query.node,
  );

  const loadMore = useCallback(() => {
    loadNext(1);
  }, [loadNext]);

  const loadMoreElementRef = usePaginatedInfiniteLoad<HTMLDivElement>(
    loadMore,
    hasNext,
    isLoadingNext,
    { rootMargin: '200px', threshold: 0 },
  );

  const jobPostingNodes = (
    jobPostingData?.jobs.edges?.map(({ node }) => node.jobPosting) ?? []
  ).filter(isTruthy);

  const jobPostingCount = jobPostingData?.jobs.count ?? 0;
  const completedJobsCount = organizationData.completedJobsCount ?? 0;

  const hideJobStats =
    // TODO: Remove redunant true after averageJobValue is fixed
    // See: https://contra-work.slack.com/archives/C06675QG0CC/p1708484534762279
    true ||
    (jobPostingCount === 0 &&
      completedJobsCount === 0 &&
      Money.deserialize(
        organizationData.averageJobValue,
      ).toUnsafeSmallestUnit() === 0);

  const jobStats = useMemo(
    () =>
      hideJobStats ? null : (
        <Overflow>
          <Grid $columns={jobPostingCount > 0 ? '3' : '2'}>
            {jobPostingCount > 0 ? (
              <Item>
                <ItemNumber>{jobPostingCount}</ItemNumber>
                <ItemDescription textStyle="subtitleRegular">
                  Active job{jobPostingCount === 1 ? '' : 's'}
                </ItemDescription>
              </Item>
            ) : null}
            <Item>
              <ItemNumber>{completedJobsCount}</ItemNumber>
              <ItemDescription textStyle="subtitleRegular">
                Project{completedJobsCount === 1 ? '' : 's'} completed
              </ItemDescription>
            </Item>
            <Item>
              <ItemNumber>
                {Money.deserialize(organizationData.averageJobValue).format()}
              </ItemNumber>
              <ItemDescription textStyle="subtitleRegular">
                Avg. job value
              </ItemDescription>
            </Item>
          </Grid>
        </Overflow>
      ),
    [
      hideJobStats,
      jobPostingCount,
      organizationData.averageJobValue,
      completedJobsCount,
    ],
  );

  if (!jobPostingData || jobPostingData.jobs.edges.length === 0) {
    return (
      <Container>
        {jobStats}
        <List>
          <OverviewJobSectionEmptyState organizationRef={organizationData} />
        </List>
      </Container>
    );
  }

  return (
    <Container>
      {jobStats}
      {visitorCanEdit ? (
        <PostAJobContainer>
          <PlusButton
            onClick={onClickPostJob}
            type="button"
          >
            <AddIcon size={24} />
          </PlusButton>
          <Text textStyle="fifthLevelHeadline">Post a job</Text>
        </PostAJobContainer>
      ) : null}

      <List>
        <WorkTabLayoutContextProvider>
          <OpportunityList>
            {jobPostingNodes.map((jobPosting, index) => {
              return (
                <Fragment key={jobPosting.id}>
                  <JobPostingCard
                    jobPostingRef={jobPosting}
                    positionIndex={index + 1}
                    visitorFilterType="ALL"
                  />
                  {index + 1 !== jobPostingNodes.length && <Divider />}
                </Fragment>
              );
            })}
          </OpportunityList>
        </WorkTabLayoutContextProvider>
      </List>
      <NewWorkOpportunitySkeleton
        isLoading={isLoadingNext}
        ref={loadMoreElementRef}
      />
    </Container>
  );
};
